import moment from 'moment';
import {
	newDeviationRequest,
	updateDeviationRequest,
	getDeviationByIdRequest,
	getCurrentClerkRequest,
	getActionTypesRequest,
	getDeviationStatusesRequest,
	getTripOrdersRequest,
} from '../requests';

import {
	throwTranslatableError,
	createDateObject,
	storageDateFormat,
	newUUID,
} from '@/core';

import rdbClient from 'rdb-client';

async function saveNewDeviation({ state, commit, dispatch }, { closeDialog }) {
	let theDeviation = { ...state.deviation }; // TODO: Try use directly
	try {
		let response = await newDeviationRequest(theDeviation);
		commit('setStateValue', { path: 'deviationHasChanged', value: true });
		if (closeDialog) {
			dispatch('closeDialog', response);
		} else commit('setEditDeviation', response);
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToSave');
	}
}

async function updateDeviation({ state, commit, dispatch }, { closeDialog }) {
	let deviationPatches = rdbClient.createPatch(state.deviationClone, state.deviation);

	if (!deviationPatches.length) {
		return true; // allreadyUpToDate
	}

	try {
		let response = await updateDeviationRequest(state.deviation.id, deviationPatches);
		commit('setStateValue', { path: 'deviationHasChanged', value: true });
		if (closeDialog) {
			dispatch('closeDialog', response);
		} else commit('setEditDeviation', response);
		state.dirty = false;
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToSave');
	}
	return false;
}

function returnDeviation({ commit }, func) {
	return (...args) => new Promise(resolve => {
		commit('setStateValue', { path: 'resolveFunc', value: resolve });
		func(...args);
	});
}

function resolveDeviation({ state, commit }, deviation) {
	if (typeof state.resolveFunc === 'function') {
		if (deviation) state.resolveFunc(deviation);
		else if (state.deviationHasChanged) state.resolveFunc(state.deviationClone);
		else state.resolveFunc(false);
		commit('setStateValue', { path: 'resolveFunc', value: undefined });
	}
}

async function loadDeviation({ commit }, id) {
	commit('setStateValue', { path: 'deviationLoaded', value: false });
	commit('setStateValue', { path: 'dialogIsShown', value: true });
	commit('setStateValue', { path: 'deviationHasChanged', value: false });
	try {
		let response = await getDeviationByIdRequest(id);
		commit('setEditDeviation', response);
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToFetch');
	}
}

function newDeviation({ commit, dispatch }, deviationPreset={}) {
	dispatch('setEditDeviation', deviationPreset);
	commit('setStateValue', { path: 'dialogIsShown', value: true });
	commit('setStateValue', { path: 'deviationHasChanged', value: false });
}

function closeDialog({ commit, dispatch }, currentDeviation) {
	dispatch('resolveDeviation', currentDeviation);
	commit('cleanState');
}

async function setEditDeviation({ commit }, deviation) {
	let clerk = deviation.clerk || await getCurrentClerkRequest();
	commit('setEditDeviation', { ...deviation, clerk });
}

async function getActionTypes() {
	return getActionTypesRequest();
}

async function getDeviationStatuses() {
	return getDeviationStatusesRequest();
}

function setDeviationType({ commit }, value) {
	commit('setDeviationValue', { path: 'deviationType', value });
}

function setStatus({ commit }, value) {
	commit('setDeviationValue', { path: 'status', value });
}

function setDateTime({ state, commit }, value) {
	let dateObject = getDateObject(state.deviation.date, value);
	commit('setDeviationValue', { path: `date`, value: dateObject });
}

function setClerk({ commit }, value) {
	commit('setDeviationValue', { path: `clerk`, value });
}

function setCustomerFault({ commit }, value) {
	commit('setDeviationValue', { path: 'customerFault', value });
}

function setMessage({ commit }, value) {
	commit('setDeviationValue', { path: 'message', value });
}

function setDeviationCause({ commit }, value) {
	commit('setDeviationValue', { path: 'deviationCause', value });
}

function setGoodsType({ commit }, value) {
	commit('setDeviationValue', { path: 'goodsType', value });
}

function setDiscoveredAt({ commit }, value) {
	commit('setDeviationValue', { path: 'discoveredAt', value });
}

function setDiscoveredBy({ commit }, value) {
	commit('setDeviationValue', { path: 'discoveredBy', value });
}

function setCustomer({ commit }, value) {
	commit('setDeviationValue', { path: 'customer', value });
}

function setVendor({ commit }, value) {
	commit('setDeviationValue', { path: 'vendor', value });
}

function setTerminal({ commit }, terminal) {
	let value = terminal;
	if (typeof terminal === 'object' && Object.keys(terminal).length === 0) value = undefined;
	commit('setDeviationValue', { path: 'terminal', value });
}

function setCost({ commit }, value) {
	commit('setDeviationValue', { path: 'cost', value });
}

function setLicencePlates({ commit }, value) {
	commit('setDeviationValue', { path: 'licencePlates', value });
}

function setTrip({ commit }, trip) {
	commit('setTrip', trip);
}

function setOrders({ commit }, value) {
	commit('setDeviationValue', { path: 'orderBindings', value });
}

function setActionType({ commit }, value) {
	commit('setDeviationValue', { path: 'actionType', value });
}

function setActionDescription({ commit }, value) {
	commit('setDeviationValue', { path: 'actionDescription', value });
}

function setActionDate({ state, commit }, value) {
	let dateObject = getDateObject(state.deviation.actionDate, value);
	commit('setDeviationValue', { path: 'actionDate', value: dateObject });
}

function addOrder({ state, dispatch }, order) {
	if (orderIsInState(state.deviation.orderBindings, order.id)) {
		console.warn(`Allready added order ${order.orderNumber}`);
	} else {
		let orderBinding = {
			id: newUUID(),
			deviationId: state.deviation.id,
			orderId: order.id,
			order,
		};
		dispatch('setOrders', [...state.deviation.orderBindings, orderBinding]);
	}
}

async function addTripOrders({ state, dispatch }) {
	if (!state.deviation.trip || !state.deviation.trip.id) {
		console.error('Trying to add orders from a non-existent trip');
		return;
	}
	let orders = await getTripOrdersRequest(state.deviation.trip.id);
	orders.forEach(order => {
		dispatch('addOrder', order);
	});
}

/* Helpers */
const getDateObject = (currentDateObj={}, { dateValue, timeValue, hasTime }) => createDateObject({
	date: dateValue ? dateValue : currentDateObj.date,
	time: timeValue ? moment(timeValue, 'LT').format('HH:mm') : moment(currentDateObj.date, storageDateFormat).format('HH:mm'),
	hasTime: (hasTime === null) ? Boolean(currentDateObj.hasTime) : hasTime,
}, Boolean(dateValue));

const orderIsInState = (orderBindings, orderId) =>
	orderBindings.reduce((acc, deviationOrder) =>
		acc || deviationOrder.orderId === orderId, false);

/* ------- */

export default {
	saveNewDeviation,
	updateDeviation,
	closeDialog,
	setEditDeviation,
	loadDeviation,
	newDeviation,
	returnDeviation,
	resolveDeviation,
	getActionTypes,
	getDeviationStatuses,
	setDeviationType,
	setStatus,
	setDateTime,
	setClerk,
	setCustomerFault,
	setMessage,
	setDeviationCause,
	setGoodsType,
	setDiscoveredAt,
	setDiscoveredBy,
	setCustomer,
	setVendor,
	setTerminal,
	setCost,
	setLicencePlates,
	setTrip,
	setOrders,
	addOrder,
	addTripOrders,
	setActionType,
	setActionDescription,
	setActionDate,
};
